<script lang="ts">
  import { getContext } from 'svelte';
  import { MyFitnessStores } from '../stores';
  import { Activity, type Exercise } from '@/Common/ActivityFunctions';
  import { cn } from '@/lib/utils';
  import TodayActivityCard from './TodayActivityCard.svelte';
  import Button from '@/lib/components/ui/button/button.svelte';
  import MergeActivitiesDialog from './MergeActivitiesDialog.svelte';
  import type { TodaysActivities } from '@/Common/constants';
  import HelpTooltip from '@/Common/HelpTooltip.svelte';

  export let todaysActivities: TodaysActivities;
  export let activityImageCarouselNumber: Record<string, number>;
  export let conflictNotes: string[] = [];

  $: activities = todaysActivities.activities.sort((a, b) => {
    return new Date(a.start_date).getTime() - new Date(b.start_date).getTime();
  });

  const { trainingPlan } = getContext<MyFitnessStores>('myFitness');

  $: forecasted = $trainingPlan.isForecastPlan ? activities.find((a) => Activity.isForecastEvent(a)) : null;
  $: planned = activities.filter((a) => Activity.isPlanned({ ...a, activity_mode: a.activity_mode}) && (!Activity.isForecast({ ...a, state: a.state }) || !$trainingPlan.isForecastPlan) && !Activity.hasMatch(a));
  $: completed = activities.filter((a) => !Activity.isPlanned({ ...a, activity_mode: a.activity_mode }));

  let selectingForMerge: boolean = false;
  let mergeDialogOpen: boolean = false;
  let activitiesForMerge: Exercise[] = [];
  let windowWidth = 0;

  function shouldShowHiddenOrDuplicate(activity: Exercise) {
    // Show hidden activities that are not duplicates, or if they are duplicates, only show if they are currently selected as the duplicate to show.
    // Show duplicate activities that are not currently selected as the duplicate to show, only if the duplicate group head is null
    return (Activity.isHidden(activity) && (!Activity.isDuplicate(activity) || activity.is_currently_selected_duplicate)) || (Activity.isDuplicate(activity) && Activity.getDuplicateGroupHead(activity, activities) === null);
  }

</script>

<svelte:window bind:innerWidth={windowWidth} />

<!-- Forecasted -->
{#if forecasted}
  <section class='mt-5'>
    <h2 class="mt-0 mb-2 text-lg font-semibold">Forecasted</h2>
    {#if !Activity.isDuplicate(forecasted)}
      <TodayActivityCard {todaysActivities} activity={forecasted} bind:activityImageCarouselNumber {conflictNotes} />
    {/if}
  </section>
{/if}

<!-- Planned -->
{#if planned.length > 0}
  <!-- If planned activities exist or no forecasted activity -->
  <section class='mt-5'>
    <h2 class="mt-0 mb-2 text-lg font-semibold">Planned</h2>
    <div class={cn('grid gap-3', planned.length > 1 ? 'xl:grid-cols-2' : '')}>
      {#each planned as p}
        {#if !Activity.isDuplicate(p)}
          <TodayActivityCard {todaysActivities} activity={p} bind:activityImageCarouselNumber />
        {/if}
      {/each}
    </div>
  </section>
{/if}

<!-- Completed -->
<section class='mt-5'>
  <div class='flex flex-row items-center gap-2'>
    <h2 class="my-2 text-lg font-semibold">Completed</h2>
    {#await Promise.all(completed.map(c => Activity.canDownload(c))) then _}
      {#if completed.filter(c => !Activity.isDuplicate(c) && !Activity.isHidden(c) && c.can_download_original_file).length > 1}
        {#if !selectingForMerge}
          <Button variant='outline' size='sm' on:click={() => { activitiesForMerge = []; selectingForMerge = true; }}>
            Merge Fit Files
            <i class='fa-solid fa-link ml-1' />
          </Button>
        {:else}
          <Button variant='outline' size='sm' on:click={() => selectingForMerge = false}>
            Cancel
          </Button>
          <Button size='sm' disabled={activitiesForMerge?.length < 2} on:click={() => {
            selectingForMerge = false; mergeDialogOpen = true;
          }}>
            Continue
          </Button>
        {/if}
        <HelpTooltip message='Select multiple consecutive FIT file activities to merge together into one.' />
      {/if}
    {/await}
  </div>
  {#if completed.length > 0}
    {#if windowWidth < 1280 || completed.filter((c) => !Activity.isDuplicate(c)).length <= 1}
      <div class='flex flex-col gap-3'>
        {#each completed as c (c.path)}
          {#if !Activity.isDuplicate(c) && !Activity.isHidden(c)}
            <TodayActivityCard {todaysActivities} activity={c} selectable={selectingForMerge && c.can_download_original_file} onSelectedChange={(selected) => { activitiesForMerge = selected ? [...activitiesForMerge, c] : activitiesForMerge.filter(a => a.path !== c.path); }} bind:activityImageCarouselNumber />
          {/if}
        {/each}
        <!-- Show hidden and duplicate activities at the bottom -->
        {#each completed as c (c.path)}
          {#if shouldShowHiddenOrDuplicate(c)}
            <TodayActivityCard {todaysActivities} activity={c} bind:activityImageCarouselNumber />
          {/if}
        {/each}
      </div>
    {:else}
      {@const completedWithHiddenAtEnd = [
        ...completed.filter(c => !Activity.isDuplicate(c) && !Activity.isHidden(c)),
        ...completed.filter(c => shouldShowHiddenOrDuplicate(c))
      ]}
      {@const completedEven = completedWithHiddenAtEnd.filter((_, i) => i % 2 === 0)}
      {@const completedOdd = completedWithHiddenAtEnd.filter((_, i) => i % 2 === 1)}
      <div class='grid grid-cols-2 gap-3'>
        <div class='flex flex-col gap-3'>
          {#each completedEven as c (c.path)}
            {#if !Activity.isDuplicate(c) && !Activity.isHidden(c)}
              <TodayActivityCard {todaysActivities} activity={c} selectable={selectingForMerge && c.can_download_original_file} onSelectedChange={(selected) => { activitiesForMerge = selected ? [...activitiesForMerge, c] : activitiesForMerge.filter(a => a.path !== c.path); }} bind:activityImageCarouselNumber />
            {/if}
          {/each}
          <!-- Show hidden and duplicate activities at the bottom -->
          {#each completedEven as c (c.path)}
            {#if shouldShowHiddenOrDuplicate(c)}
              <TodayActivityCard {todaysActivities} activity={c} bind:activityImageCarouselNumber />
            {/if}
          {/each}
        </div>
        <div class='flex flex-col gap-3'>
          {#each completedOdd as c (c.path)}
            {#if !Activity.isDuplicate(c) && !Activity.isHidden(c)}
              <TodayActivityCard {todaysActivities} activity={c} selectable={selectingForMerge && c.can_download_original_file} onSelectedChange={(selected) => { activitiesForMerge = selected ? [...activitiesForMerge, c] : activitiesForMerge.filter(a => a.path !== c.path); }} bind:activityImageCarouselNumber />
            {/if}
          {/each}
          <!-- Show hidden and duplicate activities at the bottom -->
          {#each completedOdd as c (c.path)}
            {#if shouldShowHiddenOrDuplicate(c)}
              <TodayActivityCard {todaysActivities} activity={c} bind:activityImageCarouselNumber />
            {/if}
          {/each}
        </div>
      </div>
    {/if}
  {:else}
    <div class="text-sm text-gray-500">Completed activities will appear here.</div>
  {/if}
</section>

<MergeActivitiesDialog
  activities={activitiesForMerge}
  bind:open={mergeDialogOpen}
/>
